import { computed, ref } from 'vue';
import { useHead } from '@unhead/vue';
import { decodeHtmlEntities } from './stringHelpers';

export type MetaOptions = {
  suffix?: string;
  defaultTitle?: string;
  defaultDescription?: string;
  defaultImage?: string;
  defaultUrl?: string;
};

export type SeoRawData = {
  title: string;
  description: string;
  image?: string;
};

export interface SeoData extends SeoRawData {
  path: string;
}

export const useMeta = (options: MetaOptions = {}) => {
  const {
    suffix = ' Hismile™',
    defaultTitle = 'Hismile™',
    defaultDescription = 'Hismile™',
    defaultImage = 'https://cdn.shopify.com/s/files/1/0916/4078/files/Hismile_Schema_Landscape_f825fd05-96a3-4d6e-905e-05e7cc23fd34.jpg?v=1614808923',
    defaultUrl = 'https://hismileteeth.com',
  } = options;

  const title = ref(decodeHtmlEntities(defaultTitle));
  const description = ref(decodeHtmlEntities(defaultDescription));
  const image = ref(defaultImage);
  const url = ref(defaultUrl);
  const path = ref('');

  const fullUrl = computed(() => `${url.value}${path.value}`);
  useHead({
    title: computed(() => {
      if (!title.value) return decodeHtmlEntities(defaultTitle);
      return title.value.includes('Hismile™') ? title.value : `${title.value} | ${suffix}`;
    }),
    meta: [
      {
        name: 'description',
        content: computed(() => description.value),
      },
      // Open Graph
      {
        property: 'og:title',
        content: computed(() => title.value),
      },
      {
        property: 'og:description',
        content: computed(() => description.value),
      },
      {
        property: 'og:image',
        content: computed(() => image.value),
      },
      {
        property: 'og:url',
        content: fullUrl.value,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      // Twitter
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:title',
        content: computed(() => title.value),
      },
      {
        name: 'twitter:description',
        content: computed(() => description.value),
      },
      {
        name: 'twitter:image',
        content: computed(() => image.value),
      },
    ],
    link: [
      // canonical
      {
        rel: 'canonical',
        href: computed(() => fullUrl.value),
      },
      // icon
      {
        rel: 'shortcut icon',

        href: 'https://cdn.shopify.com/s/files/1/0115/0985/8404/files/hismile_logo_01_Kgy_icon.ico?v=1618208227',
      },
      // alternate
      {
        rel: 'alternate',
        href: computed(() => `https://us.hismileteeth.com${path.value}`),
        hreflang: 'en-us',
        key: 'en-us',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://ca.hismileteeth.com${path.value}`),
        hreflang: 'en-ca',
        key: 'en-ca',
      },
      // Australia
      {
        rel: 'alternate',
        href: computed(() => `https://hismileteeth.com${path.value}`),
        hreflang: 'en-au',
        key: 'en-au',
      },
      // UK
      {
        rel: 'alternate',
        href: computed(() => `https://uk.hismileteeth.com${path.value}`),
        hreflang: 'en-gb',
        key: 'en-gb',
      },
      // EU Countries
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-de',
        key: 'en-de',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'de-de',
        key: 'de-de',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-ie',
        key: 'en-ie',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-ch',
        key: 'en-ch',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-nl',
        key: 'en-nl',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-it',
        key: 'en-it',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-no',
        key: 'en-no',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-se',
        key: 'en-se',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-be',
        key: 'en-be',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en',
        key: 'en',
      },
      {
        rel: 'alternate',
        href: computed(() => `https://eu.hismileteeth.com${path.value}`),
        hreflang: 'en-fr',
        key: 'en-fr',
      },
      // International (default)
      {
        rel: 'alternate',
        href: computed(() => `https://int.hismileteeth.com${path.value}`),
        hreflang: 'x-default',
        key: 'x-default',
      },
    ],
  });

  const setMeta = (newMeta: SeoData) => {
    if (newMeta.title) title.value = decodeHtmlEntities(newMeta.title);
    if (newMeta.description) description.value = decodeHtmlEntities(newMeta.description);
    if (newMeta.image) image.value = newMeta.image;
    if (newMeta.path) path.value = newMeta.path;
  };

  return {
    setMeta,
    title,
    description,
    image,
    url,
  };
};
